import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { Users } from "lucide-react";

import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

import {
  useAcceptMyInvitation,
  useDeclineMyInvitation,
  useMyInvitations,
} from "../api";
import { apiErrorDetail } from "../lib/errors";
import { GroupRole, type MyInvitation } from "../types";

/**
 * One notice per live group invitation addressed to the signed-in account,
 * answered in place — the dashboard twin of the notices on the Django home
 * page. Accepting lands on the consent page, like the Django flow — or, for an
 * invitation to become local partner, on the group page; refusing just removes
 * the notice. A refusal from the server (a full group closes the
 * invitation) is a toast, since the notice it came from goes away with it.
 */
export function InvitationNotices() {
  const invitations = useMyInvitations();
  if (!invitations.data?.length) return null;
  return (
    <div className="space-y-3">
      {invitations.data.map((invitation) => (
        <InvitationNotice key={invitation.id} invitation={invitation} />
      ))}
    </div>
  );
}

function InvitationNotice({ invitation }: { invitation: MyInvitation }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const accept = useAcceptMyInvitation();
  const decline = useDeclineMyInvitation();
  const busy = accept.isPending || decline.isPending;
  const isLocalPartner = invitation.assigned_group_role === GroupRole.LocalPartner;

  // Awaited rather than given per-call callbacks: the mutation settles once the
  // invitations are refetched, and that refetch drops this notice — a per-call
  // onSuccess would never run on the unmounted component.
  const onAccept = async () => {
    try {
      await accept.mutateAsync(invitation.id);
    } catch (err) {
      toast.error(apiErrorDetail(err) ?? t("dashboard.invitations.error"));
      return;
    }
    if (isLocalPartner) {
      // Sharing consents are a member's: a local partner goes to the group it
      // now helps run.
      toast.success(t("dashboard.invitations.joinedLocalPartner"));
      void navigate({ to: "/partner-group" });
      return;
    }
    toast.success(t("dashboard.invitations.joined"));
    void navigate({ to: "/consents", search: { next: pathname } });
  };
  const onDecline = () => {
    decline.mutate(invitation.id, {
      onError: (err) =>
        toast.error(apiErrorDetail(err) ?? t("dashboard.invitations.error")),
    });
  };

  return (
    <section
      aria-label={t("dashboard.invitations.label", {
        group: invitation.partner_group_name,
      })}
      className="flex gap-3 rounded-lg border border-info/30 bg-info/10 px-4 py-3 text-sm text-foreground"
    >
      <Users className="mt-0.5 size-4 shrink-0 text-info" aria-hidden />
      <div className="flex min-w-0 flex-1 flex-col gap-3 md:flex-row md:items-center">
        <p className="min-w-0 flex-1">
          {t(
            isLocalPartner
              ? "dashboard.invitations.bodyLocalPartner"
              : "dashboard.invitations.body",
            { group: invitation.partner_group_name },
          )}
        </p>
        <div className="flex shrink-0 gap-2">
          <Button size="sm" disabled={busy} onClick={() => void onAccept()}>
            {t("dashboard.invitations.accept")}
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={onDecline}>
            {t("dashboard.invitations.decline")}
          </Button>
        </div>
      </div>
    </section>
  );
}
